import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, Boxes, Users, UserRound, IndianRupee,
  Landmark, FileText, Package, Sparkles, BarChart3, LogOut, Search, Menu, X,
  ShoppingCart, Store, ArrowLeftRight, ListChecks, Undo2, Wallet, Handshake, Banknote,
  Settings, KeyRound, Save as SaveIcon, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/api";
import { toast } from "sonner";
import { AIChatFloat } from "@/components/AIChatFloat";

const NAV = [
  { to: "/", label: "Executive Dashboard", icon: LayoutDashboard, group: "Overview" },
  { to: "/projects", label: "Projects", icon: Building2, group: "Operations" },
  { to: "/inventory", label: "Sales Board", icon: Boxes, group: "Operations" },
  { to: "/sales", label: "Sales Pipeline", icon: Users, group: "Sales & CRM" },
  { to: "/sales-workflow", label: "Sales Workflow (Bookings/Receipts)", icon: FileText, group: "Sales & CRM" },
  { to: "/customers", label: "Customers", icon: UserRound, group: "Sales & CRM" },
  { to: "/land-owners", label: "Land Owners (JDA)", icon: Landmark, group: "Sales & CRM" },
  { to: "/partners", label: "Project Partners", icon: Handshake, group: "Sales & CRM" },
  { to: "/purchases", label: "Purchases", icon: ShoppingCart, group: "Procurement" },
  { to: "/vendors", label: "Vendors", icon: Store, group: "Procurement" },
  { to: "/vendor-ledger", label: "Vendor Ledger", icon: Wallet, group: "Procurement" },
  { to: "/materials", label: "Material Inventory", icon: Package, group: "Construction" },
  { to: "/material-issues", label: "Material Issues", icon: ArrowLeftRight, group: "Construction" },
  { to: "/material-returns", label: "Goods Return", icon: Undo2, group: "Construction" },
  { to: "/stock-movements", label: "Stock Movements", icon: ListChecks, group: "Construction" },
  { to: "/finance", label: "P&L", icon: IndianRupee, group: "Finance" },
  { to: "/loans", label: "Loans & Repayments", icon: Banknote, group: "Finance" },
  { to: "/expense-categories", label: "Expense Categories", icon: ListChecks, group: "Finance" },
  { to: "/users", label: "User Management", icon: ShieldCheck, group: "Administration", adminOnly: true },
  { to: "/documents", label: "Document Vault", icon: FileText, group: "Compliance" },
  { to: "/reports", label: "Reports", icon: BarChart3, group: "Insights" },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, group: "Insights" },
];

export function Layout() {
  const { user, logout, refreshUser } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();

  const grouped = NAV.filter((n) => !n.adminOnly || user?.role === "Admin").reduce((acc, n) => {
    acc[n.group] = acc[n.group] || [];
    acc[n.group].push(n);
    return acc;
  }, {});

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`${mobileOpen ? "flex" : "hidden"} lg:flex fixed lg:sticky top-0 left-0 z-40 h-screen w-52 bg-zinc-950 text-zinc-100 flex-col`}
      >
        <div className="px-3 py-3 border-b border-white/10 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-emerald-600 grid place-items-center font-heading font-extrabold text-white text-sm">P</div>
          <div className="min-w-0">
            <div className="font-heading text-sm font-bold leading-tight truncate">Param Developers</div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-400">Enterprise ERP</div>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-close">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-2">
          {Object.entries(grouped).map(([grp, items]) => (
            <div key={grp}>
              <div className="text-[9px] uppercase tracking-[0.15em] font-semibold text-zinc-500 px-2 mb-1 mt-1">{grp}</div>
              <div className="space-y-px">
                {items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
                    data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                  >
                    <n.icon size={14} strokeWidth={1.75} />
                    <span className="truncate">{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-zinc-800 grid place-items-center text-xs font-semibold">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] text-zinc-400 truncate">{user?.role}</div>
            </div>
            <button
              onClick={() => setProfileOpen(true)}
              className="ml-auto p-1 rounded hover:bg-white/10 transition-colors"
              data-testid="open-profile-btn"
              title="Profile & password"
            >
              <Settings size={13} />
            </button>
            <button
              onClick={logout}
              className="p-1 rounded hover:bg-white/10 transition-colors"
              data-testid="logout-btn"
              title="Logout"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-zinc-200">
          <div className="flex items-center gap-3 px-4 lg:px-8 py-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="sidebar-open">
              <Menu size={20} />
            </button>
            <div className="relative flex-1 max-w-xl">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                placeholder="Search projects, units, customers, documents…"
                className="pl-9 bg-zinc-50 border-zinc-200 focus-visible:ring-emerald-500 focus-visible:ring-1"
                data-testid="global-search"
              />
            </div>
            <div className="hidden md:flex items-center gap-2 text-xs text-zinc-500 font-mono">
              <span>INR ₹</span><span>·</span><span>IST</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-zinc-300"
              onClick={() => navigate("/projects/new")}
              data-testid="new-project-header"
            >
              + New Project
            </Button>
          </div>
        </header>

        <main className="flex-1 px-4 lg:px-8 py-6">
          <Outlet />
        </main>
      </div>

      <AIChatFloat />
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={user} onSaved={refreshUser} />
    </div>
  );
}


function ProfileDialog({ open, onOpenChange, user, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  React.useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setCurrent(""); setNext(""); setConfirm("");
    }
  }, [open, user]);

  async function saveName() {
    const trimmed = name.trim();
    if (trimmed.length < 2) return toast.error("Name must be at least 2 characters");
    if (trimmed === (user?.name || "")) return toast.info("Name unchanged");
    setSavingName(true);
    try {
      await api.patch("/auth/me", { name: trimmed });
      toast.success("Name updated");
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update name");
    } finally { setSavingName(false); }
  }

  async function savePassword() {
    if (!current) return toast.error("Enter current password");
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New password and confirmation don't match");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — use it next time you sign in");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    } finally { setSavingPw(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Profile & Security</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Profile name */}
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1.5">
              <UserRound size={12} /> Display Name
            </div>
            <div className="flex gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="profile-name-input"
              />
              <Button
                onClick={saveName}
                disabled={savingName}
                className="bg-emerald-700 hover:bg-emerald-800 whitespace-nowrap"
                data-testid="save-profile-name"
              >
                <SaveIcon size={13} className="mr-1" /> {savingName ? "…" : "Save"}
              </Button>
            </div>
            <div className="text-[10px] text-zinc-500">Email <b className="font-mono">{user?.email}</b> · Role <b>{user?.role}</b></div>
          </div>

          {/* Change password */}
          <div className="space-y-2 border-t border-zinc-200 pt-4">
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1.5">
              <KeyRound size={12} /> Change Password
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Current password</Label>
              <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pw-current" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">New password (min 8 chars)</Label>
              <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="pw-new" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Confirm new password</Label>
              <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pw-confirm" />
            </div>
            <Button
              onClick={savePassword}
              disabled={savingPw}
              className="w-full bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-password-btn"
            >
              <KeyRound size={13} className="mr-1" /> {savingPw ? "Updating…" : "Update Password"}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-profile">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
